import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { milestones, principles, siteInfo, stats, testimonials } from "@/mocks/site";
import SubscribeForm from "@/pages/about/components/SubscribeForm";

export default function About() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#subscribe") {
      const el = document.getElementById("subscribe");
      if (el) {
        window.setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    }
  }, [location.hash]);

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 1每日吃瓜",
        url: "https://77di.cn/about",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "1每日吃瓜",
        url: "https://77di.cn/",
        description:
          "聚焦每日吃瓜热门事件与大瓜汇总的中文内容门户，坚持克制而平衡的内容表达。",
        email: siteInfo.email,
        knowsAbout: ["娱乐热点", "剧集", "综艺", "电影", "音乐现场", "网络热议"],
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://77di.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: "关于我们",
            item: "https://77di.cn/about",
          },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: "关于我们 | 1每日吃瓜的内容原则与定位",
    description:
      "了解 1每日吃瓜：一个以克制、平衡为原则的娱乐热点内容门户。查看我们的网站定位、发展理念、内容原则、里程碑与联系方式。",
    keywords: "1每日吃瓜,关于我们,内容原则,网站定位,娱乐热点门户",
    path: "/about",
    jsonLd,
  });

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200/70 bg-background-100/60 px-4 py-10 md:px-8 md:py-14">
        <Breadcrumb items={[{ label: "关于我们" }]} />
        <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          关于 1每日吃瓜
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          我们想做的，是一份读起来不累、看完有收获的娱乐热点内容。把零散的热搜片段串成清晰的时间线，把判断权交还给读者。
        </p>
      </div>

      <section className="w-full px-4 py-14 md:px-8 md:py-20">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="h-64 w-full overflow-hidden rounded-lg md:h-[420px]">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20editorial%20desk%20scene%20with%20notebook%20and%20coffee%20on%20terracotta%20table%2C%20soft%20natural%20window%20light%2C%20minimal%20clean%20composition%2C%20high%20detail%20lifestyle%20photography&width=1000&height=800&seq=chigua-about-brand&orientation=squarish"
              alt="1每日吃瓜 内容编辑工作场景"
              title="1每日吃瓜 - 关于我们的内容原则"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
              Who We Are
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              我们是谁
            </h2>
            <div className="mt-5 space-y-4 text-base leading-loose text-foreground-700">
              <p>
                <strong className="font-semibold text-foreground-900">{siteInfo.brand}</strong> 是一个专注娱乐热点梳理的中文内容门户。
                我们不做耸动的标题，也不追逐没有依据的传闻，而是把每天被讨论最多的事件按脉络整理清楚，让你用几分钟就能掌握来龙去脉。
              </p>
              <p>
                内容覆盖剧集、综艺、电影、音乐现场、星光时尚与全网热议六个方向。无论你是想在通勤路上快速补课，还是想在周末一次性回顾当周重点，这里都能提供一个清晰的入口。
              </p>
              <p>
                我们相信，好的资讯不是替读者下结论，而是把事实和线索摆清楚，让人看得明白、想得清楚。
              </p>
            </div>
            <dl className="mt-8 grid grid-cols-2 gap-5 border-t border-background-200/80 pt-6 sm:grid-cols-4">
              {stats.map((item) => (
                <div key={item.label}>
                  <dt className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    {item.value}
                  </dt>
                  <dd className="mt-1 text-xs text-foreground-500">{item.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200/70 bg-background-100/60 px-4 py-14 md:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
              Positioning
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              网站定位与发展理念
            </h2>
          </div>
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                  <i className="ri-compass-3-line"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                  网站定位
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  介于热搜榜单与深度报道之间的轻量读物。比榜单多说一点背景，比长文更节省时间，帮你在碎片时间里看清事件全貌。
                </p>
              </div>
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-700">
                  <i className="ri-seedling-line"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                  发展理念
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  长期主义地对待内容。宁可慢一点，也要把线索核实清楚；宁可少发一条，也不写没有依据的猜测。信任比流量更重要。
                </p>
              </div>
            </div>

            <h3 className="mt-10 font-heading text-lg font-semibold text-foreground-950">
              内容原则
            </h3>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {principles.map((item) => (
                <div
                  key={item.title}
                  className="flex gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-800">
                    <i className={item.icon}></i>
                  </span>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-950">{item.title}</h4>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-14 md:px-8 md:py-20">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          一路走来
        </h2>
        <ol className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {milestones.map((item) => (
            <li
              key={item.year}
              className="rounded-lg border border-background-200/80 bg-background-50 p-5"
            >
              <span className="font-heading text-2xl font-bold text-primary-600">{item.year}</span>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="w-full border-y border-background-200/70 bg-background-100/60 px-4 py-14 md:px-8 md:py-20">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          读者怎么说
        </h2>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {testimonials.map((item) => (
            <figure
              key={item.name}
              className="rounded-lg border border-background-200/80 bg-background-50 p-5"
            >
              <div className="flex h-7 items-center gap-0.5 text-accent-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">
                {item.text}
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200/80 pt-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-semibold text-secondary-800">
                  {item.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-14 md:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
              Contact
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              联系我们
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              内容纠错、选题建议、合作咨询，都欢迎通过邮件联系。我们会在收到后尽快回复。
            </p>
            <ul className="mt-8 space-y-4">
              <li className="flex items-center gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-700">
                  <i className="ri-mail-line"></i>
                </span>
                <a
                  href={`mailto:${siteInfo.email}`}
                  rel="nofollow"
                  className="text-sm font-medium text-foreground-900 transition-colors hover:text-primary-700"
                >
                  {siteInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-800">
                  <i className="ri-time-line"></i>
                </span>
                <span className="text-sm text-foreground-700">{siteInfo.workingHours}</span>
              </li>
              <li className="flex items-center gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-700">
                  <i className="ri-chat-smile-2-line"></i>
                </span>
                <span className="text-sm text-foreground-700">
                  订阅后可直接回复邮件，我们都能收到
                </span>
              </li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <SubscribeForm />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}